/* =========================================================
   deepcalm · Redesign · Animation & Interaction Library
   - Reveal on scroll (IntersectionObserver)
   - Counter-up for big numbers
   - Bar-fill for compare bars
   - FAQ accordion
   - Variant card selection
   - Image gallery thumb-swap
   ========================================================= */

(function () {
  'use strict';

  // Idempotency: prevent double-boot if script loads twice
  if (window.__dcRedesignBooted) return;
  window.__dcRedesignBooted = true;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* -----------------------------
     Reveal on scroll
     ----------------------------- */
  function initReveal() {
    const items = document.querySelectorAll('.dc-reveal, .dc-stagger');
    if (!items.length) return;

    if (prefersReducedMotion) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0, rootMargin: '0px 0px -8% 0px' }
    );
    items.forEach((el) => {
      /* Above-the-fold: PDP-Hero-Elemente sofort smooth einblenden */
      if (el.closest('.dc-pdp-hero') && el.getBoundingClientRect().top < window.innerHeight) {
        requestAnimationFrame(() => el.classList.add('is-visible'));
      } else {
        io.observe(el);
      }
    });
  }

  /* -----------------------------
     Counter up
     Hardening: Liquid renders the target value as textContent (no-JS fallback).
     JS resets to 0 on init then animates up.
     ----------------------------- */
  function initCounters() {
    const els = document.querySelectorAll('[data-dc-counter]');
    if (!els.length) return;

    if (prefersReducedMotion) {
      // Keep text as-is (Liquid already rendered target)
      els.forEach((el) => {
        const target = parseFloat(el.dataset.dcCounter || '0');
        el.textContent = formatNumber(target, el.dataset.dcCounterDecimals);
      });
      return;
    }

    // Reset to 0 since JS is alive — animation will populate
    els.forEach((el) => { el.textContent = formatNumber(0, el.dataset.dcCounterDecimals); });

    const animateCount = (el) => {
      const target = parseFloat(el.dataset.dcCounter || '0');
      const decimals = parseInt(el.dataset.dcCounterDecimals || '0', 10);
      const duration = parseInt(el.dataset.dcCounterDuration || '900', 10);
      const start = performance.now();

      const tick = (now) => {
        const t = Math.min(1, (now - start) / duration);
        // ease-out-cubic
        const eased = 1 - Math.pow(1 - t, 3);
        const current = target * eased;
        el.textContent = formatNumber(current, decimals);
        if (t < 1) requestAnimationFrame(tick);
        else el.textContent = formatNumber(target, decimals);
      };
      requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animateCount(entry.target);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.5 }
    );
    els.forEach((el) => io.observe(el));
  }

  function formatNumber(n, decimals) {
    const d = parseInt(decimals || '0', 10);
    return n.toLocaleString('de-DE', {
      minimumFractionDigits: d,
      maximumFractionDigits: d,
    });
  }

  /* -----------------------------
     Bar fill
     Hardening: bars default to target via CSS. JS marks them pending
     (hides) then animates on viewport intersection. No-JS = bars at target.
     ----------------------------- */
  function initBars() {
    const bars = document.querySelectorAll('.dc-bar-fill');
    if (!bars.length) return;

    // Mark all bars as JS-controlled — CSS will hide them via .dc-bar-pending
    bars.forEach((b) => b.classList.add('dc-bar-pending'));

    if (prefersReducedMotion) {
      // Skip animation: just reveal at target
      requestAnimationFrame(() => {
        bars.forEach((b) => b.classList.add('is-visible'));
      });
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.4 }
    );
    bars.forEach((b) => io.observe(b));
  }

  /* -----------------------------
     FAQ accordion (with aria-expanded sync)
     ----------------------------- */
  function initFAQ() {
    document.querySelectorAll('.dc-faq__q').forEach((btn) => {
      btn.addEventListener('click', () => {
        const item = btn.closest('.dc-faq__item');
        if (!item) return;
        const isOpen = item.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', String(isOpen));
      });
    });
  }

  /* -----------------------------
     Variant card selection
     - Mirrors a hidden Shopify variant input via data-dc-variant-id
     ----------------------------- */
  function initVariantCards() {
    const cards = document.querySelectorAll('[data-dc-variant-card]');
    if (!cards.length) return;

    /* Abo-Feld setzen: Plan vorhanden -> Abo, sonst deaktiviert -> Einmalkauf */
    function applySellingPlan(card, group) {
      const planInput = document.querySelector(`[data-dc-selling-plan-input="${group}"]`);
      if (!planInput) return;
      const planId = card.dataset.dcSellingPlan || '';
      if (planId) {
        planInput.value = planId;
        planInput.disabled = false;
      } else {
        planInput.value = '';
        planInput.disabled = true; /* deaktivierte Felder werden nicht mitgesendet */
      }
    }

    cards.forEach((card) => {
      card.addEventListener('click', () => {
        const group = card.dataset.dcVariantGroup;
        document.querySelectorAll(`[data-dc-variant-group="${group}"]`).forEach((c) => {
          c.classList.remove('is-selected');
          c.setAttribute('aria-checked', 'false');
        });
        card.classList.add('is-selected');
        card.setAttribute('aria-checked', 'true');

        const variantId = card.dataset.dcVariantId;
        if (variantId) {
          const hiddenInput = document.querySelector(`[data-dc-variant-input="${group}"]`);
          if (hiddenInput) {
            hiddenInput.value = variantId;
            hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
        applySellingPlan(card, group);
      });
    });

    /* Init: vorausgewählte Karte direkt anwenden (Abo-Feld korrekt setzen) */
    document.querySelectorAll('[data-dc-variant-card].is-selected').forEach((card) => {
      applySellingPlan(card, card.dataset.dcVariantGroup);
    });
  }

  /* -----------------------------
     PDP gallery thumb swap
     ----------------------------- */
  function initGallery() {
    const galleries = document.querySelectorAll('[data-dc-gallery]');
    galleries.forEach((g) => {
      const main = g.querySelector('[data-dc-gallery-main] img');
      const thumbs = g.querySelectorAll('[data-dc-gallery-thumb]');
      if (!main || !thumbs.length) return;

      /* Thumb-KLICK wird vom kanonischen Inline-Script in dc-r-pdp-hero.liquid behandelt
         (inkl. srcset/sizes-Entfernung + Lightbox). Hier KEIN zweiter Click-Listener mehr,
         sonst doppelte Listener + main.src-ohne-srcset-Race (Flackern/falsches Bild). */

      /* Thumbs-Pagination: Pfeile scrollen um ~4 Thumbs (eine Reihe) — nur hier implementiert */
      const wrap  = g.querySelector('[data-dc-thumbs-wrap]');
      const track = g.querySelector('[data-dc-thumbs-track]');
      if (wrap && track) {
        const prev = wrap.querySelector('[data-dc-thumbs-prev]');
        const next = wrap.querySelector('[data-dc-thumbs-next]');
        const step = () => {
          const first = track.querySelector('.dc-pdp-hero__thumb');
          if (!first) return track.clientWidth;
          const styles = window.getComputedStyle(track);
          const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
          /* 4 Thumbs auf einmal scrollen */
          return (first.offsetWidth + gap) * 4;
        };
        if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
        if (next) next.addEventListener('click', () => track.scrollBy({ left:  step(), behavior: 'smooth' }));
      }
    });
  }

  /* -----------------------------
     Add-to-Cart — Event-Delegation auf document.
     Robust gegen Section-Re-Renders im Theme-Editor (alte Listener auf neuem DOM tot).
     Native form submit als Fallback fuer Browser ohne fetch.
     ----------------------------- */
  function initAddToCart() {
    if (window.__dcATCBound) return;
    window.__dcATCBound = true;

    /* Klick direkt abfangen statt erst beim submit: Shopify meldet product_added_to_cart
       sonst zweimal (einmal beim Absenden des Formulars, einmal bei der Antwort von
       /cart/add.js) -> zwei AddToCart bei Meta pro Klick. Ohne fetch laeuft das
       Formular normal weiter. */
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-dc-add-to-cart]');
      if (!btn || !btn.form || !window.fetch) return;
      e.preventDefault();
      handleAdd(btn.form, btn);
    });

    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      var btn = form.querySelector('[data-dc-add-to-cart]');
      if (!btn) return;
      if (!window.fetch) { btn.disabled = true; return; }

      e.preventDefault();
      handleAdd(form, btn);
    });

    function handleAdd(form, btn) {
      if (btn.disabled) return;
      btn.disabled = true;
      btn.classList.add('is-loading');

      /* Sicherheits-Timeout: der Button erholt sich IMMER, auch wenn ein
         Redirect oder der Fetch haengt. Verhindert den ewigen Spinner. */
      var safetyTimer = setTimeout(function () { resetBtn(); }, 10000);

      function resetBtn() {
        clearTimeout(safetyTimer);
        btn.disabled = false;
        btn.classList.remove('is-loading');
      }

      var formData   = new FormData(form);
      var returnTo   = formData.get('return_to');
      /* Ohne Direkt-Checkout: Drawer-Abschnitte mitschicken, sonst kann der Drawer nicht befuellt werden */
      var drawerEl = document.querySelector('cart-drawer');
      if (!returnTo && drawerEl && typeof drawerEl.getSectionsToRender === 'function') {
        formData.append('sections', drawerEl.getSectionsToRender().map(function (s) { return s.id; }));
        formData.append('sections_url', window.location.pathname);
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
        body: formData
      })
        .then(function (res) {
          return res.json().then(function (data) { return { ok: res.ok, status: res.status, data: data }; });
        })
        .then(function (result) {
          if (!result.ok) {
            console.error('[ATC] /cart/add.js failed', result.status, result.data);
            resetBtn();
            window.location.href = '/cart';
            return;
          }
          /* Direct-to-checkout: kurz warten, damit Shopifys product_added_to_cart
             (kommt ~50 ms nach der Antwort) vor dem Seitenwechsel rausgeht */
          if (returnTo) {
            setTimeout(function () { window.location.href = returnTo; }, 500);
            return;
          }
          /* Fallback: Cart-Drawer oeffnen */
          var cartDrawer = document.querySelector('cart-drawer');
          var hasDrawer  = cartDrawer && typeof cartDrawer.renderContents === 'function';
          if (hasDrawer && result.data && result.data.sections) {
            if (cartDrawer.classList.contains('is-empty')) cartDrawer.classList.remove('is-empty');
            try { cartDrawer.renderContents(result.data); } catch (err) {
              console.error('[ATC] renderContents threw', err);
              window.location.href = '/cart';
              return;
            }
            try {
              if (typeof cartDrawer.open === 'function') cartDrawer.open();
            } catch (_) {}
            resetBtn();
          } else {
            window.location.href = '/cart';
          }
        })
        .catch(function (err) {
          console.error('[ATC] fetch threw', err);
          resetBtn();
          window.location.href = '/cart';
        });
    }

    /* Fix: Button nach Zurueck-Navigation zuruecksetzen.
       Nach dem Weiterleiten in den Checkout bleibt der Button disabled +
       is-loading. Geht der Nutzer mit "Zurueck" auf die PDP, kommt die Seite
       aus dem bfcache in genau diesem Zustand -> Spinner dreht ewig, kein
       zweiter Klick moeglich. pageshow feuert auch beim bfcache-Restore und
       setzt den Button wieder frei. */
    window.addEventListener('pageshow', function () {
      document.querySelectorAll('[data-dc-add-to-cart]').forEach(function (btn) {
        btn.disabled = false;
        btn.classList.remove('is-loading');
      });
    });
  }

  /* -----------------------------
     Boot
     ----------------------------- */
  function boot() {
    initReveal();
    initCounters();
    initBars();
    initFAQ();
    initVariantCards();
    initGallery();
    initAddToCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
